import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, AlertCircle, Database, RefreshCw } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitFileStep, { STEP, stepIcon } from './toolkit/ToolkitFileStep';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import useToolkitBatch, { sourceRow } from './toolkit/useToolkitBatch';
import useOnPrem, { rowsOf } from './toolkit/useOnPrem';
import ToolkitResyncUdf, { useResyncUdf } from './toolkit/ToolkitResyncUdf';
import ToolkitWriteConfirm, { confirmed } from './toolkit/ToolkitWriteConfirm';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › AR Accounts.
//
// Ported from the desktop toolkit's OHIP › AR Accounts, FILE mode: an Opera v5 AR Accounts report (PR_WITH_AR_NO
// XML) becomes Opera Cloud AR accounts. Per account the server (toolkit ar.js migrate-account) runs the desktop's
// steps in order — check the number, find the profile by its v5 NAME_ID, create it if missing, add an AR ADDRESS,
// create the account, read it back. An account already in Opera Cloud is skipped, never re-created.
//
// Or the DATABASE source (the desktop's database mode): the accounts are read from the property's OPERA v5 through
// the project's SyncConnect (toolkit onprem.js ar-accounts-migrate). A profile not in Opera Cloud yet is then NOT
// created from the row — it is re-sent from v5 through OXI (WRITES.resync-profiles, step 4: a write, confirmed by the
// project name), so Cloud gets OXI's own linked profile with all its data, and the row is run again once it lands.
//
// SETTINGS FIRST, as on the desktop: three Cashiering controls must be off or OPERA renumbers / duplicates the
// accounts and the v5 numbers are lost. The file cannot be loaded until they pass.
//
// THE PROJECT'S CONNECTION. Everything goes through the sync project's own OPERA Cloud connector — its property, and
// its EXTERNALSYSTEM code for the v5 name ids on migrated profiles — so nothing is chosen here.
//
// RESUMABLE. Rows and their results are kept in toolkit storage under the project's property, so a run stopped
// half way — or picked up by a colleague — carries on from where it was.

const COLUMNS = [
    { key: 'ACCOUNT_NO', label: 'AR No' },
    { key: 'ACCOUNT_NAME', label: 'Name' },
    { key: 'PROFILE_TYPE', label: 'Type' },
    { key: 'NAME_ID', label: 'v5 Name ID' },
    { key: 'COUNTRY', label: 'Country' },
    { key: 'cloudProfileId', label: 'Cloud profile' },
    { key: 'cloudArAccountId', label: 'Cloud account' },
];
const SEARCH_KEYS = ['ACCOUNT_NO', 'ACCOUNT_NAME', 'NAME_ID', 'cloudProfileId'];
const RESYNC_BATCH = 200;      // WRITES.resync-profiles takes at most 200 profiles a request

// An ar-accounts-migrate row (lower-case) as the file parser's row, so migrate-account takes either.
const fromDatabase = (r) => ({
    NAME_ID: r.name_id, ACCOUNT_NO: r.account_no, ACCOUNT_NAME: r.account_name, ACCOUNT_TYPE: r.account_type,
    CREDIT_LIMIT: r.credit_limit, BALANCE: r.balance, PHONE: r.phone, EMAIL: r.email, PROFILE_TYPE: r.profile_type,
    ADDRESS_TYPE: r.address_type, ADDRESS1: r.address1, ADDRESS2: r.address2, CITY: r.city, STATE: r.state,
    ZIP_CODE: r.zip_code, COUNTRY: r.country, SOURCE: 'database',
});

// What is stored per row: the row without its step log (large, and only useful for the run that produced it).
// (status and message are kept: the stored row is the resume point.)
const storable = (row) => ({ ...sourceRow(row), status: row.status, errorMessage: row.errorMessage });

const arRequest = (row) => ({ area: 'ar', op: 'migrate-account', args: { account: storable(row), resyncMissing: row.SOURCE === 'database' } });
const AR_SERVER = { screen: 'ar-accounts', title: 'AR Accounts', keyOf: (row) => row.ACCOUNT_NO, save: storable, afterItem: 'save-ar-account' };

function ToolkitArAccountsPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready, project } = toolkit;
    const { ask, write } = useOnPrem(ctx);
    const resync = useResyncUdf(ctx, ready);
    const [settings, setSettings] = useState({ state: STEP.IDLE, result: null, message: '' });
    const [loadMsg, setLoadMsg] = useState('');
    const [dbLoad, setDbLoad] = useState({ state: STEP.IDLE, message: '' });
    const [resend, setResend] = useState({ state: STEP.IDLE, message: '' });
    const [confirm, setConfirm] = useState('');
    const saveRow = useCallback((row) =>
        toolkitService.storage(ctx, 'save-ar-account', { account: storable(row) }), [ctx]);

    // On the server, the stored account is kept current by the job (afterItem save-ar-account), as saveRow does here.
    // Run on server, for accounts read from v5 with a UDF chosen and the project name typed (step 4): the job re-sends
    // the profiles still missing from Opera Cloud itself, waits for OXI, and migrates those accounts again (jobs.js
    // flow ar-v5-resync — up to 3 rounds, 15 minutes apart). Without them it migrates and leaves re-send pending.
    const batch = useToolkitBatch({ request: arRequest, ctx, onRowDone: saveRow, server: { ...AR_SERVER,
        extra: () => (autoResend()
            ? { confirm, options: { flow: { name: 'ar-v5-resync', udfField: resync.udf, rounds: 3, waitSeconds: 900 } } }
            : {}) } });
    const { load } = batch;

    // Pick up whatever was stored for the project's property (a run in progress, or finished).
    useEffect(() => {
        if (!ready) { load([]); return; }
        setLoadMsg('');
        toolkitService.storage(ctx, 'get-all-ar-accounts', {})
            .then(({ data }) => {
                const saved = data.result || [];
                load(saved.map((r) => ({ ...r, status: String(r.status || 'pending').toLowerCase() })));
                if (saved.length) setLoadMsg(`${saved.length} account(s) restored from the last run at this property.`);
            })
            .catch((err) => setLoadMsg(err?.response?.data?.message || 'Could not load saved accounts for this property.'));
    }, [ready, ctx, load]);

    const validateSettings = useCallback(async () => {
        if (!ready) return false;
        setSettings({ state: STEP.BUSY, result: null, message: 'Checking Opera Cloud AR settings…' });
        try {
            const { data } = await toolkitService.ar(ctx, 'validate-account-settings');
            const r = data.result;
            setSettings({ state: r.valid ? STEP.DONE : STEP.ERROR, result: r,
                message: r.valid ? 'All AR account settings are correct.' : 'Fix these Opera Cloud settings before loading the file:' });
            return r.valid;
        } catch (err) {
            setSettings({ state: STEP.ERROR, result: null, message: err?.response?.data?.message || err.message });
            return false;
        }
    }, [ready, ctx]);

    const onParsed = useCallback(async (rows) => {
        load(rows);
        setLoadMsg('');
        try {
            await toolkitService.storage(ctx, 'bulk-save-ar-accounts', { accounts: rows.map(storable) });
        } catch (err) {
            setLoadMsg(`Loaded, but not saved for resuming: ${err?.response?.data?.message || err.message}`);
        }
    }, [ctx, load]);

    const loadFromDatabase = async () => {
        if (!(await validateSettings())) return;
        setDbLoad({ state: STEP.BUSY, message: 'Asking SyncConnect for the property\'s AR accounts…' });
        try {
            const d = await ask('ar-accounts-migrate');
            if (d.status !== 'RETURNED') { setDbLoad({ state: STEP.ERROR, message: d.error_message || `No answer yet (${d.status}) — try again shortly.` }); return; }
            const rows = rowsOf(d.result).filter((r) => r.account_no).map(fromDatabase);
            await onParsed(rows);
            setDbLoad({ state: STEP.DONE, message: `${rows.length} active AR account(s) read from Opera v5${d.result_truncated === 'Y' ? ' — the answer was cut short, so not every account is here' : ''}.` });
        } catch (e) {
            setDbLoad({ state: STEP.ERROR, message: e?.response?.data?.message || e.message });
        }
    };

    // Re-send every resync-pending row's profile from v5 through OXI, a batch of 200 a request. The rows stay
    // resync-pending: running them again finds the profile once OXI has delivered it.
    const pendingIds = [...new Set(batch.rows.filter((r) => r.status === 'resync-pending').map((r) => String(r.NAME_ID)).filter(Boolean))];
    const fromV5Open = batch.rows.some((r) => r.SOURCE === 'database' && !['success', 'skipped'].includes(r.status));
    function autoResend() { return fromV5Open && !!resync.udf && confirmed(project, confirm); }
    const resendProfiles = async () => {
        setResend({ state: STEP.BUSY, message: `Re-sending ${pendingIds.length} profile(s)…` });
        let sent = 0;
        const problems = [];
        for (let i = 0; i < pendingIds.length; i += RESYNC_BATCH) {
            const ids = pendingIds.slice(i, i + RESYNC_BATCH);
            try {
                const d = await write('resync-profiles', { nameIds: ids, udfField: resync.udf }, confirm, { ids });
                if (d.status !== 'RETURNED') { problems.push(d.error_message || `batch ${i / RESYNC_BATCH + 1}: no answer yet (${d.status})`); continue; }
                sent += (d.writeOutcome?.items || []).filter((x) => x.resynced).length;
                if (d.writeOutcome?.status !== 'success') problems.push(d.writeOutcome?.message);
            } catch (e) {
                problems.push(e?.response?.data?.message || e.message);
            }
            setResend({ state: STEP.BUSY, message: `Re-sent ${sent} of ${pendingIds.length} so far…` });
        }
        setConfirm('');
        setResend({ state: problems.length ? STEP.ERROR : STEP.DONE,
            message: `${sent} of ${pendingIds.length} profile(s) re-sent through OXI. Give OXI a few minutes, then Resume to run those accounts again.${problems.length ? ` Problems: ${problems.filter(Boolean).join('; ')}` : ''}` });
    };

    const clear = async () => {
        await toolkitService.storage(ctx, 'clear-ar-accounts', {}).catch(() => {});
        load([]);
        setLoadMsg('');
    };

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="AR Accounts"
                subtitle="Create Opera Cloud AR accounts from an Opera v5 AR Accounts report (XML) — the profile is found by its v5 name ID or created, given an AR address, and the account created with its v5 number." />

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">1</span> Opera Cloud AR settings {stepIcon(settings.state)}</div>
                <Button variant="secondary" onClick={validateSettings} disabled={!ready || settings.state === STEP.BUSY}>
                    <ShieldCheck className="icon" /> Validate settings
                </Button>
                {settings.message && <p className={`tkb-msg${settings.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{settings.message}</p>}
                {!!settings.result?.errors?.length && (
                    <ul className="tkb-warn-list">
                        {settings.result.errors.map((e) => (
                            <li key={e.setting}><AlertCircle size={13} /> <strong>{e.setting}</strong>
                                {e.current != null ? ` is ${e.current}, should be ${e.expected}` : ''} — {e.message}</li>
                        ))}
                    </ul>
                )}
            </div>

            <ToolkitFileStep number={2} title="Opera v5 AR Accounts report" label="Choose AR Accounts XML"
                kind="ar-accounts" onParsed={onParsed} beforeUpload={validateSettings} disabled={!ready}>
                {loadMsg && <p className="tkb-msg">{loadMsg}</p>}
            </ToolkitFileStep>

            <div className="tkb-step">
                <div className="tkb-step-head"><span className="tkb-num">3</span> …or read them from Opera v5 {stepIcon(dbLoad.state)}</div>
                <Button variant="secondary" onClick={loadFromDatabase} disabled={!ready || dbLoad.state === STEP.BUSY || batch.running}>
                    <Database className="icon" /> Read AR accounts through SyncConnect
                </Button>
                {dbLoad.message && <p className={`tkb-msg${dbLoad.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{dbLoad.message}</p>}
                <p className="tkb-hint">Accounts read this way re-send a missing profile from v5 through OXI instead of creating it.</p>
            </div>

            {(pendingIds.length > 0 || fromV5Open) && (
                <div className="tkb-step">
                    <div className="tkb-step-head"><span className="tkb-num">4</span> Re-send missing profiles {stepIcon(resend.state)}</div>
                    <ToolkitResyncUdf resync={resync} disabled={!ready} />
                    <ToolkitWriteConfirm project={project} value={confirm} onChange={setConfirm}
                        what={pendingIds.length
                            ? `${pendingIds.length} profile(s) will have ${resync.udf || 'the chosen UDF'} set to PROCESSED_<time> and be saved in v5, which re-sends each one through OXI to Opera Cloud.`
                            : `Profiles not yet in Opera Cloud will have ${resync.udf || 'the chosen UDF'} set to PROCESSED_<time> and be saved in v5, which re-sends each one through OXI.`} />
                    {pendingIds.length > 0 && (
                        <Button variant="success" onClick={resendProfiles} style={{ marginTop: '0.6rem' }}
                            disabled={!ready || !resync.udf || !confirmed(project, confirm) || resend.state === STEP.BUSY || batch.running}>
                            <RefreshCw className="icon" /> Re-send {pendingIds.length} profile(s)
                        </Button>
                    )}
                    {resend.message && <p className={`tkb-msg${resend.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{resend.message}</p>}
                    <p className="tkb-hint">
                        {autoResend()
                            ? 'Run on server will re-send the missing profiles itself, wait for OXI, and migrate those accounts again (up to 3 rounds, 15 minutes apart).'
                            : 'Choose the UDF and type the project name, and Run on server will also re-send the missing profiles itself and retry those accounts.'}
                    </p>
                </div>
            )}

            <ToolkitBatchTable batch={batch} columns={COLUMNS} searchKeys={SEARCH_KEYS} exportName="AR_Accounts"
                canRun={ready} onClear={clear} />
        </div>
    );
}

export default ToolkitArAccountsPage;
